
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/api';
import { useUser } from '../context/UserContext';
import { notifyManagerReviewReady } from '../lib/notifications';
import { selectPriorAnnualCycle } from '../lib/reviewCycles';

const B = {
  black: '#1e1e23', surface: '#26262d', surfaceLight: '#2e2e36',
  border: '#1e1a2e', borderLight: '#2e2850',
  purple: '#7B5CC8', purpleDim: '#4a3880', purpleDeep: '#1a1030',
  gold: '#C9A84C', goldDim: '#8a6e2a',
  textPrimary: '#ffffff', textSecondary: '#8888aa', textMuted: '#444466',
  green: '#1D9E75', greenDeep: '#0a1f15', greenDim: '#0f6e56',
};

const PRIORITY_META = [
  { key: 'must', label: 'Must do', bg: '#0f0810', border: '#4a1530', tag: '#c0392b', desc: 'Critical this half' },
  { key: 'should', label: 'Should do', bg: '#0f0e08', border: '#4a3a10', tag: '#C9A84C', desc: 'Important, not urgent' },
  { key: 'could', label: 'Could do', bg: '#080f0e', border: '#0e3a2a', tag: '#1D9E75', desc: 'Nice to have' },
];

const GOAL_STATUSES = [
  { key: 'done', label: 'Done', color: '#1D9E75', bg: '#0a1f15', border: '#0f6e56' },
  { key: 'in_progress', label: 'In Progress', color: '#C9A84C', bg: '#0f0d05', border: '#8a6e2a' },
  { key: 'not_started', label: 'Not Started', color: '#444466', bg: '#11111a', border: '#1e1a2e' },
  { key: 'deprioritised', label: 'Deprioritised', color: '#8888aa', bg: '#0a0a0f', border: '#2e2850' },
];

const TABS = ['Past priorities', 'Next priorities', 'Review & submit'];

const inputStyle = { width: '100%', boxSizing: 'border-box', fontSize: 13, resize: 'vertical', padding: '10px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'rgba(255,255,255,0.03)', color: B.textPrimary, fontFamily: 'inherit', lineHeight: 1.6 };

const emptyProgress = () => ({ status: null, update: '' });

export default function MidYearForm() {
  const { employee } = useUser();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [cycle, setCycle] = useState(null);
  const [annualCycle, setAnnualCycle] = useState(null);
  const [review, setReview] = useState(null);
  const [annualReview, setAnnualReview] = useState(null);
  const [progress, setProgress] = useState({ must: emptyProgress(), should: emptyProgress(), could: emptyProgress() });
  const [next, setNext] = useState({ must: '', should: '', could: '' });
  const [reviewerName, setReviewerName] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const setProgressField = (key, field, val) => setProgress(p => ({ ...p, [key]: { ...p[key], [field]: val } }));

  const progressPct = Math.round(([
    Object.values(progress).every(p => p.status && p.update.trim().length > 5),
    Object.values(next).every(v => v.trim().length > 5),
  ].filter(Boolean).length / 2) * 100);

  const hasContent = Object.values(progress).some(p => p.status || p.update.trim()) || Object.values(next).some(v => v.trim());

  useEffect(() => { fetchData(); }, [employee]);

  const fetchData = async () => {
    if (!employee) return;
    setLoading(true);

    // Get open mid-year cycle
    const { data: midCycle } = await supabase.from('review_cycles').select('*').eq('is_open', true).eq('type', 'Mid-Year').single();
    if (!midCycle) { setLoading(false); return; }
    setCycle(midCycle);

    // Get the annual review this mid-year reflects on. It must be the most
    // recent *prior* Annual cycle (year < this mid-year's year), NOT simply
    // the newest Annual cycle — otherwise an in-progress Annual for the
    // current year would shadow last year's completed review. See
    // selectPriorAnnualCycle + reviewCycles.test.js.
    // The lt('year', ...) filter narrows the fetch server-side to prior years;
    // selectPriorAnnualCycle then picks the newest of those. The helper still
    // enforces the year bound itself (defense in depth, and it stays correct
    // in isolation for its unit tests).
    const { data: annCycles } = await supabase.from('review_cycles').select('*').eq('type', 'Annual').lt('year', midCycle.year).order('year', { ascending: false });
    const annCycle = selectPriorAnnualCycle(annCycles, midCycle);
    if (annCycle) {
      setAnnualCycle(annCycle);
      const { data: annRev } = await supabase.from('annual_reviews').select('*').eq('employee_id', employee.id).eq('cycle_id', annCycle.id).single();
      if (annRev) setAnnualReview(annRev);
    }

    // Get existing mid-year review
    const { data: existing } = await supabase.from('midyear_reviews').select('*').eq('employee_id', employee.id).eq('cycle_id', midCycle.id).single();
    if (existing) {
      setReview(existing);
      setProgress({
        must: { status: existing.must_status || null, update: existing.must_update || '' },
        should: { status: existing.should_status || null, update: existing.should_update || '' },
        could: { status: existing.could_status || null, update: existing.could_update || '' },
      });
      setNext({ must: existing.next_must || '', should: existing.next_should || '', could: existing.next_could || '' });
      if (existing.emp_status === 'submitted') setSubmitted(true);
    }

    // If HR set a reviewer override that differs from the Okta manager,
    // look up the reviewer's display name so the submit-confirm copy can
    // accurately say "Your reviewer [Name]" instead of falsely showing
    // the org-chart manager_name. Falls back to email if the lookup is
    // empty (employees RLS may hide the row from the current user).
    const reviewerEmail = employee.review_manager_email;
    if (reviewerEmail && reviewerEmail.toLowerCase() !== (employee.manager_email || '').toLowerCase()) {
      const { data: reviewer } = await supabase
        .from('employees').select('name').eq('email', reviewerEmail).maybeSingle();
      setReviewerName(reviewer?.name || null);
    }

    setLoading(false);
  };

  const flash = msg => { setSaveMsg(msg); setTimeout(() => setSaveMsg(''), 2500); };

  const buildPayload = (status) => ({
    employee_id: employee.id,
    cycle_id: cycle.id,
    must_status: progress.must.status,
    must_update: progress.must.update,
    should_status: progress.should.status,
    should_update: progress.should.update,
    could_status: progress.could.status,
    could_update: progress.could.update,
    next_must: next.must,
    next_should: next.should,
    next_could: next.could,
    emp_status: status,
    updated_at: new Date().toISOString(),
  });

  // Persist the mid-year review row, resilient to the unique
  // (employee_id, cycle_id) constraint. `review` can be null in state even
  // though a row already exists — e.g. the employee hit Save Draft earlier
  // this session (previously we never captured that inserted row) or has the
  // form open in another tab. A blind insert then throws "duplicate key value
  // violates unique constraint midyear_reviews_employee_id_cycle_id_key".
  //
  // So resolve the target row FIRST (from state, else a lookup) and update it;
  // only insert when none exists. We intentionally do NOT swallow write errors
  // here — a genuine insert/update failure still surfaces the global toast.
  // The update also keys on employee_id + cycle_id so a stale id in memory can
  // never touch a row outside this employee+cycle (RLS already blocks other
  // employees; this guards against a wrong-cycle id). Returns { data, error }.
  const persistReview = async (payload) => {
    let rowId = review?.id;
    if (!rowId) {
      const { data: existing } = await supabase.from('midyear_reviews')
        .select('id').eq('employee_id', employee.id).eq('cycle_id', cycle.id).maybeSingle();
      rowId = existing?.id ?? null;
    }
    if (!rowId) {
      return supabase.from('midyear_reviews').insert(payload).select().single();
    }
    // NB: no .single() on the UPDATE — .single() appends limit=1, and PostgREST
    // rejects a *limited* UPDATE without an explicit order ("A limit was applied
    // without an explicit order"). return=representation (via .select()) still
    // returns the affected row; the id+employee_id+cycle_id filter matches at
    // most one, so we unwrap the first match ourselves.
    const { data, error } = await supabase.from('midyear_reviews').update(payload)
      .eq('id', rowId).eq('employee_id', employee.id).eq('cycle_id', cycle.id).select();
    return { data: Array.isArray(data) ? (data[0] ?? null) : data, error };
  };

  const saveDraft = async () => {
    if (!employee || !cycle || !hasContent) return;
    setSaving(true);
    const { data, error } = await persistReview(buildPayload('in_progress'));
    if (!error) {
      if (data) setReview(data); // capture the row so the next write UPDATEs, never re-inserts
      const now = new Date(); setSavedAt(`${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`); flash('Draft saved');
    }
    setSaving(false);
  };

  const submitReview = async () => {
    if (!employee || !cycle) return;
    setSaving(true);
    const { data, error } = await persistReview({ ...buildPayload('submitted'), submitted_at: new Date().toISOString() });
    if (!error) {
      if (data) setReview(data);
      // Notify the effective reviewer — review_manager_email override wins,
      // else fall back to the Okta manager_email.
      const reviewer = employee.review_manager_email || employee.manager_email;
      if (reviewer) await notifyManagerReviewReady({ managerEmail: reviewer, employeeName: employee.name, cycleLabel: cycle.label });
      setSubmitted(true);
    }
    setSaving(false);
  };

  const annPriorities = { must: annualReview?.must_do, should: annualReview?.should_do, could: annualReview?.could_do };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: B.black, color: B.textSecondary, fontSize: 12, letterSpacing: '0.08em' }}>LOADING...</div>;
  if (!cycle) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: B.black, fontFamily: 'inherit' }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: B.textSecondary, marginBottom: 16 }}>No mid-year review cycle is currently open.</p>
        <button onClick={() => navigate('/')} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '8px 20px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>← BACK TO PORTAL</button>
      </div>
    </div>
  );

  const Header = () => (
    <div style={{ background: B.black, borderBottom: `0.5px solid ${B.border}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate('/')} aria-label="Go to home" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>

          <img src="/logo-full.png" alt="xLight" style={{ height: 22, width: 'auto', display: 'block' }} />

        </button>
        <span style={{ width: 1, height: 16, background: B.border, margin: '0 8px' }} />
        <span style={{ fontSize: 11, color: B.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase' }}>People</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {savedAt && <span style={{ fontSize: 11, color: B.textMuted }}>Draft saved at {savedAt}</span>}
        {saveMsg && <span style={{ fontSize: 11, color: B.green }}>{saveMsg}</span>}
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 11, color: B.textSecondary, margin: 0, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{cycle?.label}</p>
          <p style={{ fontSize: 12, color: B.textPrimary, margin: '2px 0 0' }}>{employee?.name} · {employee?.department}</p>
        </div>
      </div>
    </div>
  );

  // Effective reviewer for the submit-confirm copy. When HR has set a
  // review_manager_email override that differs from the Okta manager, show
  // that person ("Your reviewer …"); otherwise keep "Your manager …".
  const overrideReviewer = !!employee?.review_manager_email
    && (employee.review_manager_email || '').toLowerCase() !== (employee?.manager_email || '').toLowerCase();
  const reviewerLabel = overrideReviewer ? 'Your reviewer' : 'Your manager';
  const reviewerDisplayName = overrideReviewer
    ? (reviewerName || employee.review_manager_email)
    : (employee?.manager_name || 'your manager');

  if (submitted) return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header />
      <div style={{ padding: '3rem 2rem', textAlign: 'center', maxWidth: 460, margin: '0 auto' }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purple}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: 20, color: B.purple }}>✓</div>
        <h2 style={{ fontSize: 18, fontWeight: 400, color: B.textPrimary, marginBottom: 10 }}>Mid-year review submitted</h2>
        <p style={{ fontSize: 13, color: B.textSecondary, lineHeight: 1.7, marginBottom: 24 }}>{reviewerLabel} ({reviewerDisplayName}) has been notified and will complete the mid-year assessment shortly.</p>
        <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '1rem 1.25rem', textAlign: 'left', marginBottom: 20 }}>
          <p style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>Priority progress summary</p>
          {PRIORITY_META.map(({ key, label, tag }) => {
            const gs = GOAL_STATUSES.find(s => s.key === progress[key].status);
            return (
              <div key={key} style={{ padding: '10px 0', borderBottom: key !== 'could' ? `0.5px solid ${B.border}` : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 99, background: tag, color: '#fff' }}>{label.toUpperCase()}</span>
                  {gs && <span style={{ fontSize: 11, color: gs.color }}>{gs.label}</span>}
                </div>
                <p style={{ fontSize: 12, color: (progress[key].update || '').trim() ? B.textPrimary : B.textMuted, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', fontStyle: (progress[key].update || '').trim() ? 'normal' : 'italic' }}>{(progress[key].update || '').trim() || 'No update provided'}</p>
              </div>
            );
          })}
        </div>
        {Object.values(next).some(v => (v || '').trim()) && (
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '1rem 1.25rem', textAlign: 'left', marginBottom: 20 }}>
            <p style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>Next priorities</p>
            {PRIORITY_META.map(({ key, label, tag }) => (
              <div key={key} style={{ padding: '10px 0', borderBottom: key !== 'could' ? `0.5px solid ${B.border}` : 'none' }}>
                <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 99, background: tag, color: '#fff' }}>{label.toUpperCase()}</span>
                <p style={{ fontSize: 12, color: (next[key] || '').trim() ? B.textPrimary : B.textMuted, lineHeight: 1.6, margin: '6px 0 0', whiteSpace: 'pre-wrap', fontStyle: (next[key] || '').trim() ? 'normal' : 'italic' }}>{(next[key] || '').trim() || 'Not provided'}</p>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => navigate('/')} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 24px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: B.textPrimary, cursor: 'pointer' }}>← BACK TO PORTAL</button>
      </div>
    </div>
  );

  const SaveDraftBtn = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <button onClick={saveDraft} disabled={!hasContent || saving}
        style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 20px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: hasContent ? B.textSecondary : B.textMuted, cursor: hasContent ? 'pointer' : 'not-allowed' }}>
        {saving ? 'SAVING...' : 'SAVE DRAFT'}
      </button>
      {saveMsg && <span style={{ fontSize: 11, color: B.green }}>{saveMsg}</span>}
    </div>
  );

  return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header />
      <div style={{ background: B.surface, borderBottom: `0.5px solid ${B.border}`, padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex' }}>
          {TABS.map((t, i) => (
            <button key={t} onClick={() => setTab(i)}
              style={{ fontSize: 12, letterSpacing: '0.05em', color: tab === i ? B.textPrimary : B.textSecondary, background: 'none', border: 'none', borderBottom: tab === i ? `1.5px solid ${B.purple}` : '1.5px solid transparent', padding: '10px 14px', cursor: 'pointer' }}>
              {i < tab ? '✓ ' : ''}{t.toUpperCase()}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 64, height: 2, background: B.border, borderRadius: 99 }}>
            <div style={{ width: `${progressPct}%`, height: '100%', background: B.purple, borderRadius: 99 }} />
          </div>
          <span style={{ fontSize: 11, color: B.textSecondary }}>{progressPct}%</span>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '2rem 1.5rem' }}>

        {/* TAB 1 — Past priorities */}
        {tab === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ background: B.purpleDeep, border: `0.5px solid ${B.purpleDim}`, borderRadius: 8, padding: '12px 16px', display: 'flex', gap: 10 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: B.purple, marginTop: 5, flexShrink: 0 }} />
              <p style={{ fontSize: 12, color: B.textSecondary, lineHeight: 1.6, margin: 0 }}>
                These priorities were set in your <span style={{ color: B.textPrimary }}>{annualCycle?.label || 'annual review'}</span>. Reflect on your progress against each one.
              </p>
            </div>
            {PRIORITY_META.map(({ key, label, tag, bg, border }) => {
              const gs = GOAL_STATUSES.find(s => s.key === progress[key].status);
              return (
                <div key={key} style={{ background: bg, border: `0.5px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem' }}>
                  <span style={{ fontSize: 10, padding: '2px 10px', borderRadius: 99, background: tag, color: '#fff', letterSpacing: '0.06em', display: 'inline-block', marginBottom: 10 }}>{label.toUpperCase()}</span>
                  <div style={{ background: 'rgba(0,0,0,0.3)', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
                    <p style={{ fontSize: 10, letterSpacing: '0.07em', color: B.textSecondary, marginBottom: 4, textTransform: 'uppercase' }}>Original goal</p>
                    <p style={{ fontSize: 13, color: annPriorities[key] ? B.textPrimary : B.textMuted, lineHeight: 1.6, margin: 0, fontStyle: annPriorities[key] ? 'normal' : 'italic' }}>{annPriorities[key] || 'No annual priority recorded'}</p>
                  </div>
                  <p style={{ fontSize: 10, letterSpacing: '0.07em', color: B.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>Progress status</p>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                    {GOAL_STATUSES.map(s => (
                      <button key={s.key} onClick={() => setProgressField(key, 'status', s.key)}
                        style={{ fontSize: 11, letterSpacing: '0.05em', padding: '5px 12px', borderRadius: 99, border: `0.5px solid ${progress[key].status === s.key ? s.color : B.border}`, background: progress[key].status === s.key ? s.bg : 'transparent', color: progress[key].status === s.key ? s.color : B.textSecondary, cursor: 'pointer' }}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <p style={{ fontSize: 10, letterSpacing: '0.07em', color: B.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>Progress update</p>
                  <textarea value={progress[key].update} onChange={e => setProgressField(key, 'update', e.target.value)} rows={3} placeholder="What happened? What did you achieve, learn or encounter?" style={{ ...inputStyle, border: `0.5px solid ${border}` }} />
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <SaveDraftBtn />
              <button onClick={() => setTab(1)} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 22px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: B.textPrimary, cursor: 'pointer' }}>NEXT →</button>
            </div>
          </div>
        )}

        {/* TAB 2 — Next priorities */}
        {tab === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: B.purpleDeep, border: `0.5px solid ${B.purpleDim}`, borderRadius: 8, padding: '12px 16px', display: 'flex', gap: 10 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: B.purple, marginTop: 5, flexShrink: 0 }} />
              <p style={{ fontSize: 12, color: B.textSecondary, lineHeight: 1.6, margin: 0 }}>Set your priorities for the <span style={{ color: B.textPrimary }}>next 6 months</span>. These will carry forward into your next review cycle.</p>
            </div>
            {PRIORITY_META.map(({ key, label, tag, bg, border }) => (
              <div key={key} style={{ background: bg, border: `0.5px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 10, padding: '2px 10px', borderRadius: 99, background: tag, color: '#fff', letterSpacing: '0.06em' }}>{label.toUpperCase()}</span>
                  <span style={{ fontSize: 11, color: B.textSecondary }}>{key === 'must' ? 'Critical this half' : key === 'should' ? 'Important, not urgent' : 'Nice to have'}</span>
                </div>
                <textarea value={next[key]} onChange={e => setNext(n => ({ ...n, [key]: e.target.value }))} rows={3} placeholder={`List your ${label.toLowerCase()} priorities for the next 6 months...`} style={{ ...inputStyle, border: `0.5px solid ${border}` }} />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => setTab(0)} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 22px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'none', color: B.textSecondary, cursor: 'pointer' }}>← BACK</button>
                <SaveDraftBtn />
              </div>
              <button onClick={() => setTab(2)} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 22px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: B.textPrimary, cursor: 'pointer' }}>NEXT →</button>
            </div>
          </div>
        )}

        {/* TAB 3 — Review & submit */}
        {tab === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 13, color: B.textSecondary, lineHeight: 1.7 }}>Review your responses before submitting. {reviewerLabel} will be notified once submitted.</p>
            <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, textTransform: 'uppercase', marginBottom: 4 }}>Past priority progress</p>
            {PRIORITY_META.map(({ key, label, tag }) => {
              const s = GOAL_STATUSES.find(x => x.key === progress[key].status);
              return (
                <div key={key} style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 99, background: tag, color: '#fff' }}>{label.toUpperCase()}</span>
                    {s && <span style={{ fontSize: 11, color: s.color, padding: '1px 10px', borderRadius: 99, background: s.bg, border: `0.5px solid ${s.border}` }}>{s.label}</span>}
                  </div>
                  <p style={{ fontSize: 13, color: progress[key].update ? B.textPrimary : B.textMuted, lineHeight: 1.6, margin: 0, fontStyle: progress[key].update ? 'normal' : 'italic' }}>{progress[key].update || 'No update provided'}</p>
                </div>
              );
            })}
            <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, textTransform: 'uppercase', marginTop: 8, marginBottom: 4 }}>Next 6-month priorities</p>
            <div style={{ display: 'flex', gap: 10 }}>
              {PRIORITY_META.map(({ key, label, tag, bg, border }) => (
                <div key={key} style={{ flex: 1, background: bg, border: `0.5px solid ${border}`, borderRadius: 8, padding: '10px 12px' }}>
                  <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 99, background: tag, color: '#fff', display: 'inline-block', marginBottom: 8 }}>{label.toUpperCase()}</span>
                  <p style={{ fontSize: 12, color: next[key] ? B.textPrimary : B.textMuted, lineHeight: 1.5, margin: 0, fontStyle: next[key] ? 'normal' : 'italic' }}>{next[key] || 'Empty'}</p>
                </div>
              ))}
            </div>
            <div style={{ background: B.purpleDeep, border: `0.5px solid ${B.purpleDim}`, borderRadius: 8, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: B.purple, flexShrink: 0 }} />
              <p style={{ fontSize: 12, color: B.textSecondary, margin: 0 }}>On submit, <span style={{ color: B.textPrimary }}>{reviewerDisplayName}</span> will be notified to complete the mid-year manager review.</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => setTab(1)} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 22px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'none', color: B.textSecondary, cursor: 'pointer' }}>← BACK</button>
                <SaveDraftBtn />
              </div>
              <button onClick={submitReview} disabled={saving}
                style={{ fontSize: 12, letterSpacing: '0.08em', padding: '10px 28px', borderRadius: 6, border: 'none', background: B.purple, color: '#fff', cursor: 'pointer' }}>
                {saving ? 'SUBMITTING...' : 'SUBMIT MID-YEAR REVIEW'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
